'use client';

import { useState, useCallback } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { LayoutGrid, Plus, X, RotateCw } from 'lucide-react';
import { toast } from 'sonner';

import { usePageBuilderContext } from '../page-builder-context';
import { uploadScreenshot } from '@/lib/services/uploadService';
import type { AsyncUpload } from '@/lib/hooks/use-async-uploads';
import type { GalleryBlock } from '@/lib/types/page-builder';
import type { UploadedImage } from '@/lib/validations/product-submission';
import { MaterialSpinner } from '@/components/ui/material-spinner';
import { cn } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

interface GalleryBlockEditorProps {
  block: GalleryBlock;
}

// ============================================================================
// CROPPING HELPER
// ============================================================================

async function getCroppedImage(
  imageSrc: string,
  croppedAreaPixels: Area
): Promise<File> {
  const image = new Image();
  image.src = imageSrc;
  await new Promise((resolve) => { image.onload = resolve; });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2d context');

  canvas.width = croppedAreaPixels.width;
  canvas.height = croppedAreaPixels.height;

  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    croppedAreaPixels.width,
    croppedAreaPixels.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      const file = new File([blob], `gallery-${Date.now()}.webp`, { type: 'image/webp' });
      resolve(file);
    }, 'image/webp', 0.99);
  });
}

// ============================================================================
// GALLERY IMAGE SLOT
// ============================================================================

interface GallerySlotProps {
  image?: UploadedImage;
  index: number;
  onUpload: (file: File) => void;
  onRemove: () => void;
}

function GallerySlot({ image, index, onUpload, onRemove }: GallerySlotProps) {
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith('image/')) {
      onUpload(file);
    }
  };

  if (image) {
    return (
      <div className="relative group aspect-square rounded-lg overflow-hidden bg-muted">
        <img
          src={image.originalUrl}
          alt={`Gallery image ${index + 1}`}
          className="w-full h-full object-cover"
        />
        <button
          type="button"
          onClick={onRemove}
          className={cn(
            'absolute top-2 right-2 w-6 h-6',
            'bg-black/50 hover:bg-destructive text-white rounded-full',
            'flex items-center justify-center',
            'opacity-0 group-hover:opacity-100 transition-opacity'
          )}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      onClick={() => document.getElementById(`gallery-input-${index}`)?.click()}
      className={cn(
        'aspect-square rounded-lg border-2 border-dashed',
        'flex items-center justify-center cursor-pointer',
        'transition-all duration-200',
        isDragging ? 'border-foreground/30 bg-muted/50 scale-[1.02]' : 'border-border'
      )}
    >
      <input
        type="file"
        id={`gallery-input-${index}`}
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
          e.target.value = '';
        }}
        className="sr-only"
      />

      <Plus className="h-6 w-6 text-foreground-tertiary" />
    </div>
  );
}

// ============================================================================
// PENDING SLOT (optimistic preview while the upload runs in the background)
// ============================================================================

interface PendingSlotProps {
  upload: AsyncUpload;
  onRetry: () => void;
  onDismiss: () => void;
}

function PendingSlot({ upload, onRetry, onDismiss }: PendingSlotProps) {
  return (
    <div className="relative aspect-square rounded-lg overflow-hidden bg-muted">
      <img
        src={upload.previewUrl}
        alt="Загружаемое изображение"
        className="w-full h-full object-cover"
      />

      {upload.status === 'uploading' && (
        <div className="absolute top-2 right-2 rounded-full bg-card/95 p-1.5">
          <MaterialSpinner className="h-4 w-4" />
        </div>
      )}

      {upload.status === 'error' && (
        <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2 p-2">
          <button
            type="button"
            onClick={onRetry}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:bg-primary-hover text-primary-foreground text-xs font-medium rounded-lg"
          >
            <RotateCw className="h-3 w-3" />
            Повторить
          </button>
          <span className="text-[11px] text-white/70 text-center">
            Не удалось загрузить
          </span>
          <button
            type="button"
            onClick={onDismiss}
            className="absolute top-2 right-2 w-6 h-6 bg-black/50 hover:bg-destructive text-white rounded-full flex items-center justify-center"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function GalleryBlockEditor({ block }: GalleryBlockEditorProps) {
  const { updateBlockWith, uploads, startUpload, retryUpload, dismissUpload } =
    usePageBuilderContext();

  // Cropper state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const images = block.content.images;
  const maxImages = 4;

  // In-flight uploads belonging to this gallery (each gets its own slot)
  const uploadKeyPrefix = `gallery:${block.id}:`;
  const pendingUploads = Object.entries(uploads).filter(([key]) =>
    key.startsWith(uploadKeyPrefix)
  );

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const openCropper = (file: File) => {
    const url = URL.createObjectURL(file);
    setImageToCrop(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCropModalOpen(true);
  };

  const handleCropSave = async () => {
    if (!imageToCrop || !croppedAreaPixels) return;

    try {
      const croppedFile = await getCroppedImage(imageToCrop, croppedAreaPixels);
      setCropModalOpen(false);
      URL.revokeObjectURL(imageToCrop);
      setImageToCrop(null);

      // Fire-and-track: the cropped preview appears instantly in its own
      // slot while the upload runs in the background
      startUpload(
        `${uploadKeyPrefix}${crypto.randomUUID()}`,
        croppedFile,
        uploadScreenshot,
        (data) => ({
          warmUrl: data.originalUrl,
          commit: () =>
            updateBlockWith(block.id, (b) => {
              const content = (b as GalleryBlock).content;
              return { content: { ...content, images: [...content.images, data] } };
            }),
        })
      );
    } catch (e) {
      console.error('Error:', e);
      toast.error(e instanceof Error ? e.message : 'Ошибка обработки');
    }
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
      setImageToCrop(null);
    }
  };

  const handleRemove = (index: number) => {
    updateBlockWith(block.id, (b) => {
      const content = (b as GalleryBlock).content;
      return {
        content: { ...content, images: content.images.filter((_, i) => i !== index) },
      };
    });
  };

  // Derive columns from image count (same logic as preview & detail page)
  const totalImages = images.length + pendingUploads.length;
  const canAdd = totalImages < maxImages;
  const totalSlots = totalImages + (canAdd ? 1 : 0);
  const derivedColumns = totalSlots <= 2 ? 2 : totalSlots === 3 ? 3 : 4;

  return (
    <>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-foreground-secondary">
            <LayoutGrid className="h-4 w-4" />
            <span>Галерея</span>
          </div>
          <span className="text-xs text-foreground-tertiary">
            {totalImages}/{maxImages}
          </span>
        </div>

        {/* Image grid — matches preview layout */}
        <div
          className="grid gap-2"
          style={{
            gridTemplateColumns: `repeat(${derivedColumns}, minmax(0, 1fr))`,
          }}
        >
          {images.map((image, index) => (
            <GallerySlot
              key={image.key || index}
              image={image}
              index={index}
              onUpload={openCropper}
              onRemove={() => handleRemove(index)}
            />
          ))}
          {pendingUploads.map(([key, upload]) => (
            <PendingSlot
              key={key}
              upload={upload}
              onRetry={() => retryUpload(key)}
              onDismiss={() => dismissUpload(key)}
            />
          ))}
          {canAdd && (
            <GallerySlot
              index={totalImages}
              onUpload={openCropper}
              onRemove={() => {}}
            />
          )}
        </div>

        {/* Help text */}
        {totalImages < 2 && (
          <p className="text-xs text-foreground-tertiary text-center">
            Добавьте минимум 2 изображения
          </p>
        )}
      </div>

      {/* Crop Modal */}
      {cropModalOpen && imageToCrop && (
        <div className="fixed inset-0 z-50 bg-black">
          <div className="absolute inset-0 bottom-16">
            <Cropper
              image={imageToCrop}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              showGrid={true}
            />
          </div>

          <div className="absolute bottom-0 left-0 right-0 bg-black/90 backdrop-blur border-t border-white/10 px-4 py-3">
            <div className="max-w-lg mx-auto flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1">
                <span className="text-xs text-white/60">−</span>
                <input
                  type="range"
                  min={0.5}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1 h-1 bg-white/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
                />
                <span className="text-xs text-white/60">+</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCropCancel}
                  className="px-3 py-1.5 text-sm font-medium text-white/80 hover:text-white"
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={handleCropSave}
                  className="px-4 py-1.5 text-sm font-semibold text-primary-foreground bg-primary hover:bg-primary-hover rounded-lg"
                >
                  Готово
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
